import React, { useState, useCallback } from 'react';
import { useListContext, useNotify, useRefresh } from 'react-admin';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';

import { initiatePricing } from './initiatePricing';
import { requirePartner } from '@src/providers/dataProvider/shared';
import type { PricingType } from './types';

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

/**
 * Status banner showing apply status and Apply button.
 * Shows when pricing rules have been modified but not yet applied.
 */
interface ConditionDiscountsStatusBannerProps {
  pricingType: PricingType;
}

const ConditionDiscountsStatusBanner = ({ pricingType }: ConditionDiscountsStatusBannerProps) => {
  const { meta } = useListContext();
  const notify = useNotify();
  const refresh = useRefresh();
  const [isApplying, setIsApplying] = useState(false);

  const initiatedAt = meta?.initiatedAt as string | null | undefined;
  const processedAt = meta?.processedAt as string | null | undefined;

  const handleApply = useCallback(async () => {
    setIsApplying(true);
    try {
      const partner = requirePartner();
      await initiatePricing({ partnerId: partner.id, pricingType });
      notify('Pricing rules are being applied', { type: 'success' });
      refresh();
    } catch (error) {
      console.error('ConditionDiscountsStatusBanner: handleApply failed', {
        pricingType,
        error: error instanceof Error ? error.message : error,
      });
      const message = error instanceof Error ? error.message : 'Failed to apply pricing rules';
      notify(message, { type: 'error' });
    } finally {
      setIsApplying(false);
    }
  }, [notify, pricingType, refresh]);

  // Determine status
  const isProcessing = initiatedAt && !processedAt;
  const isProcessed = initiatedAt && processedAt;
  // Show unapplied state when not processing and not processed (i.e., initiatedAt is null)
  const showUnappliedState = !initiatedAt;

  return (
    <Paper sx={{ p: 2, mt: 2, mb: 2, mx: 2 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box>
          {showUnappliedState && (
            <Alert severity="warning" sx={{ py: 0 }}>
              <Typography variant="body2">
                <strong>Unapplied changes pending.</strong>
                {' '}
                Click Apply to process pricing rules.
              </Typography>
            </Alert>
          )}
          {isProcessing && (
            <Alert severity="info" sx={{ py: 0 }}>
              <Typography variant="body2">
                <strong>INITIATED:</strong>
                {' '}
                {formatDate(initiatedAt)}
                {' '}
                — Processing in progress...
              </Typography>
            </Alert>
          )}
          {isProcessed && (
            <Alert severity="success" sx={{ py: 0 }}>
              <Typography variant="body2">
                <strong>Processed:</strong>
                {' '}
                {formatDate(processedAt)}
              </Typography>
            </Alert>
          )}
        </Box>
        <Button
          variant="contained"
          color="primary"
          onClick={handleApply}
          disabled={isApplying || !!initiatedAt}
          startIcon={isApplying ? <CircularProgress size={16} color="inherit" /> : undefined}
        >
          {isApplying ? 'Applying...' : 'Apply'}
        </Button>
      </Box>
    </Paper>
  );
};

export default ConditionDiscountsStatusBanner;
